import {Contract} from "./place-order-wizard/models/Contract";

export function extractTradeDetails(whatIf: any, today: Date) {
    const positions = whatIf.tradeDetails.concat(whatIf.equityTradeDetails || []);
    return whatIf.trades
                 .map((trade: Trade) => createTradeDetail(trade, positions, today))
                 .filter((tr: Trade | null) => tr != null);
}

function createTradeDetail(trade: any, positions: Trade[], today: Date): any {
    const position = positions.find((pos: any) => pos.id === trade.id);
    if (position == null) {
        return null;
    }
    const contract = Contract.from(position);
    let quantity = trade.quantity;
    if (contract.isEquity()) {
        quantity = quantity / contract.cvf;
    }

    return {
        code: trade.code,
        quantity,
        price: contract.theo,
        id: position.id,
        manual: true,
        tradeDate: null,
        include: true,
        contract
    };
}

// todo: move to Common place.
function calcGcd(a: number, b: number) {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b > 0) {
        const temp = b;
        b = a % (b);
        a = temp;
    }
    return a;
}

/**
 * @param {object[]} trades - whatIf.trades or tmcOrder.legs
 * @returns {number} GCD
 */
export function calcGcdFromTrades(trades: any[]) {
    const quantities = trades.map((tr) => tr.quantity);
    return gcdOf(quantities);
}

export function gcdOf(quantities: number[]) {
    let gcd = quantities[0];
    quantities.forEach((q) => {
        gcd = calcGcd(gcd, q);
    });
    return gcd;
}

/**
 *
 *
 * @param {object[]} trades - whatIf.trades or tmcOrder.legs
 * @param {boolean} validateOnlyLegRate - if true, it validates only legs.quentity and rate.
 *                                          Otherwise it validates all properties
 * @returns {string} return error description if it is not valid for TMC. return null if it is validate.
 */
export function validateTradesForTmc(trades: any[], validateOnlyLegRate: boolean) {
    if (!trades || !trades.length || trades.length < 2) {
        return "TMC needs 2 legs at least.";
    }
    if (trades.length > 4) {
        return "There must be no more than 4 legs.";
    }
    if (!validateOnlyLegRate) {
        const equityCode = trades[0].code.substr(0, 3);
        if (trades.some((t) => t.code.substr(0, 3) !== equityCode)) {
            return "All legs must belong to the same underlying.";
        }
        if (trades.filter((t) => t.length <= 3).length > 1) {
            return "It allows to have only one Equity leg.";
        }
    }

    const gcd = calcGcdFromTrades(trades);

    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < trades.length; i++) {
        const qty = trades[i].quantity;
        if (qty === 0) {
            return "TMC Legs cannot have a ratio of 0";
        }
        const normalized = qty / gcd;
        if (normalized % 1 !== 0 || Math.abs(normalized) > 4) {
            return "All legs must have a ratio of no more than 1:4.";
        }
    }
    return null;
}

/**
 * @param {object[]} trades Trades built by extractTradeDetails()
 * @returns {object} new tmcOrder object.
 * @throws {Error} validation fail
 */
export function buildTmcOrder(code: string, trades: any[], opposite: boolean, tmcSpread: any): any {
    const validationError = validateTradesForTmc(trades, false);
    if (validationError) {
        throw new Error(validationError);
    }
    const orderLots = calcGcdFromTrades(trades);

    const cvf = trades.map((trade: any) => trade.contract)
                      .find((contract: any) => contract.cvf != null).cvf;

    const premium = trades.reduce((acc, trade) => {
        const legPremium = trade.spread.theo * cvf * trade.quantity;
        return acc + legPremium;
    }, 0);

    const tmcLegs = trades.map((t) => {
        const l = t; // Object.assign({}, t); // it is shallow copy!
        l.isTmcLeg = true;
        l.tmcRatio = l.quantity / orderLots;
        if (l.code.length === 3) {
            l.tmcRatio = l.tmcRatio * cvf;
        }
        l.underlying = t.contract.underlying || t.code;

        const legCvf = l.contract.cvf || cvf;

        l.premium = l.spread.theo * legCvf * l.quantity;

        return l;
    });

    return {
        code,
        opposite,
        underlying: tmcLegs[0].underlying,
        legs: tmcLegs,
        theoPrice: tmcSpread.theo,
        limit: tmcSpread.mid,
        bid: tmcSpread.bid,
        bidLimit: tmcSpread.bidLimit,
        ask: tmcSpread.ask,
        askLimit: tmcSpread.askLimit,
        quantity: orderLots,
        theo: tmcSpread.theo,
        premium: Math.round(premium * 1000) / 1000
    };
}

export function buildTmcLegs(trades: any[]): Array<any> {
    const gcd = calcGcdFromTrades(trades);
    return trades.map((t) => {
        return {
            code: t.code,
            ratio: t.quantity / gcd
        };
    });
}

export class TicketUtils {
    public getTradesFromWhatIf = extractTradeDetails;
    public calcGcd = calcGcd;
    public calcGcdFromTrades = calcGcdFromTrades;
    public validateTradesForTmc = validateTradesForTmc;
    public buildTmcOrder = buildTmcOrder;
}
